import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import { DateBadge } from '@cocoonspace/ui/components/cocoon/date-badge'
import { Form, FormField } from '@cocoonspace/ui/components/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLingui } from '@lingui/react/macro'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import * as z from 'zod'

import { FactuInfosRecap } from '~/domains/account/components/factus-info-recap'
import { PersoInfosRecap } from '~/domains/account/components/perso-infos-recap'
import { useBookingVisitMutation } from '~/domains/booking/hooks/use-booking-visit-mutation.hook'
import { PaymentMethodSelector } from '~/domains/payments/components/payment-method-selector'
import { useGetDefaultPaymentMethod } from '~/domains/payments/hooks/use-get-default-payment-method.hook'
import { useHttpErrorMsg } from '~/lib/get-http-error-msg'
import { useAuth } from '~/providers/auth-provider'
import { useUserContext } from '~/providers/user-provider'
import { useBookingVisitStore } from '../space-visit.store'

const formValuesSchema = z.object({
	day: z.string(),
	end: z.string(),
	paymentMethod: z.string(),
	start: z.string(),
})

type FormValues = z.infer<typeof formValuesSchema>

export const PaymentStepAuth = ({ space }: { space: Space }) => {
	const router = useRouter()
	const { t } = useLingui()
	const httpErrorMsg = useHttpErrorMsg()

	const { isInfoFactuIncomplete, isInfosPersoIncomplete, isIncompleteProfile } =
		useUserContext()

	const visitBookingMutation = useBookingVisitMutation()

	const { data: defaultPaymentMethod } = useGetDefaultPaymentMethod()

	const day = useBookingVisitStore.use.day()
	const startTime = useBookingVisitStore.use.startTime()

	const form = useForm<FormValues>({
		resolver: zodResolver(formValuesSchema),
		values: {
			day,
			end: dateTimeUtils.utils.addDurationToTime(startTime!, 30),
			paymentMethod: defaultPaymentMethod?.id || '',
			start: startTime || '',
		},
	})

	const formValues = form.watch()

	const onSubmit = ({ start, end, day, paymentMethod }: FormValues) => {
		const query = visitBookingMutation
			.mutateAsync({
				day,
				end,
				paymentMethod,
				space: space.id,
				start,
			})
			.then((data) => {
				router.push(`/events/${data.event.id}`)
			})

		toast.promise(query, {
			loading: t`Chargement...`,
			success: t`Votre visite a été réservée avec succès !`,
			error: (err) => {
				const errorCode = err?.response?.data?.errors?.[0].code as
					| string
					| undefined

				return httpErrorMsg[`api.${errorCode}`]
					? httpErrorMsg[`api.${errorCode}`]()
					: t`Une erreur est survenue`
			},
		})
	}

	return (
		<div className='flex max-w-[350px] flex-col gap-4'>
			<Card className='flex items-stretch divide-x overflow-hidden border'>
				<DateBadge
					day={day}
					tz={space.timezone}
				/>

				<div className='flex flex-1 flex-col justify-center px-4'>
					<p className='truncate text-lg font-bold'>{space.title}</p>

					<div className='flex items-center'>
						{t`De ${formValues.start?.replace(':', 'h').replace('h00', 'h')} à ${formValues.end?.replace(':', 'h').replace('h00', 'h')}`}
					</div>
				</div>
			</Card>

			{isInfosPersoIncomplete && (
				<Card className='border p-4'>
					<PersoInfosRecap />
				</Card>
			)}

			{!isInfosPersoIncomplete && isInfoFactuIncomplete && (
				<Card className='border p-4'>
					<FactuInfosRecap />
				</Card>
			)}

			<Form {...form}>
				<form
					noValidate
					onSubmit={form.handleSubmit(onSubmit)}
				>
					<div className='flex flex-col gap-2'>
						<div className='font-bold'>{t`Mon moyen de paiement`}</div>

						<FormField
							control={form.control}
							name='paymentMethod'
							render={({ field }) => (
								<PaymentMethodSelector
									onChange={field.onChange}
									value={field.value}
								/>
							)}
						/>
					</div>

					<Button
						className='mt-4 w-full'
						disabled={
							visitBookingMutation.isPending ||
							isIncompleteProfile ||
							!formValues.paymentMethod
						}
						type='submit'
					>
						{t`Valider`}
					</Button>
				</form>
			</Form>
		</div>
	)
}

export const PaymentStep = ({ space }: { space: Space }) => {
	const { t } = useLingui()
	const { isAuth, openLogin } = useAuth()

	useEffect(() => {
		if (!isAuth) {
			openLogin()
		}
		// oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
	}, [isAuth])

	if (!isAuth) {
		return (
			<div className='flex flex-col gap-4'>
				{t`Vous devez être connecté pour réserver une visite`}
				<Button onClick={() => openLogin()}>{t`Se connecter`}</Button>
			</div>
		)
	}

	return <PaymentStepAuth space={space} />
}
